import ApiHosts from "@/libs/ApiHosts";
import { COUNTRY_ALIASES } from "@/libs/countries/country-names";
import { withUpstreamTiming } from "@/libs/diagnostics/upstream-timing";

export interface ShippingCountry {
  countryCallingCodes: string[];
  countryCode: string;
  englishCountryName: string;
  koreanCountryName: string;
  ownCountryName: string;
  region: string;
  supportedLanguages: string[];
  searchText?: string; // 국가 검색을 용이하게 하기위해 내부적으로 추가
}

const url = `${ApiHosts.admin}/api/v1/shipping/countries`;

export async function getShippingCountries(): Promise<ShippingCountry[]> {
  try {
    const countries = await withUpstreamTiming(
      "shipping-countries",
      async () => {
        const response = await fetch(url);
        if (!response.ok) return null;
        return (await response.json()) as ShippingCountry[];
      },
      { describe: (list) => ({ count: list?.length }) },
    );

    if (!countries) {
      return [];
    }

    const _countries: ShippingCountry[] = countries.map(
      (country): ShippingCountry => {
        const baseNames = [
          country.koreanCountryName,
          country.ownCountryName,
          country.englishCountryName,
          country.countryCode,
          ...(country.countryCallingCodes ?? []).flatMap((code) => [
            code,
            `+${code}`,
          ]),
        ].filter(Boolean);

        const aliasNames = COUNTRY_ALIASES[country.countryCode] ?? [];
        const searchText = baseNames.concat(aliasNames).join(" ").toLowerCase();

        return {
          ...country,
          searchText,
        };
      },
    );
    return _countries;
  } catch (e) {
    console.error("getShippingCountries error:", e);
    return [];
  }
}

export function groupCountriesByRegion(
  countries: ShippingCountry[],
): Record<string, ShippingCountry[]> {
  const regionOrder = [
    "Americas",
    "Europe",
    "Asia Pacific",
    "Africa",
    "Middle East",
  ];

  const sortedCountries = countries.slice().sort((a, b) => {
    const indexA = regionOrder.indexOf(a.region);
    const indexB = regionOrder.indexOf(b.region);

    const normalizedA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
    const normalizedB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;

    return normalizedA - normalizedB;
  });

  return sortedCountries.reduce<Record<string, ShippingCountry[]>>(
    (acc, country) => {
      (acc[country.region] ??= []).push(country);
      return acc;
    },
    {},
  );
}

const FIVE_MINUTES = 1000 * 60 * 5;
let cache: { expiresAt: number; data: ShippingCountry[] } | null = null;

export async function getCachedShippingCountries(): Promise<ShippingCountry[]> {
  const now = Date.now();
  if (cache && cache.expiresAt > now) return cache.data;

  const data = await getShippingCountries();
  cache = { expiresAt: now + FIVE_MINUTES, data };
  return data;
}

/**
 * 배송가능국 country code Set. 비배송국 결제 차단/교정 가드(미들웨어·`/api/order/checkout`)의 단일 출처.
 *
 * ⚠️ 조회 실패 시 **빈 Set** 을 반환한다 (throw 하지 않음 — `getCachedShippingCountries` 가 실패 시 `[]`).
 * 따라서 호출처는 `size === 0` 을 "목록 미확보 → fail-open" 신호로 다뤄야 한다. 빈 Set 을
 * "전부 비배송" 으로 해석하면 모든 국가가 차단/교정되는 footgun.
 */
export async function getShippableCountryCodeSet(): Promise<Set<string>> {
  const countries = await getCachedShippingCountries();
  return new Set(countries.map((c) => c.countryCode));
}
